import * as React from 'react'

/**
 * A stable, per-instance DOM id.
 *
 * `React.useId` is not reachable here: the runtime is React 18 and has it, but
 * this repo pins `@types/react` 17 (the `resolutions` block in package.json),
 * which does not declare it — the same reason `src/design-mode/useDesignMode`
 * cannot use `useSyncExternalStore`. rc-util solves it the same way for antd
 * itself (`rc-util/es/hooks/useId`), so this is the ordinary fallback, not a
 * workaround.
 *
 * A `useRef` rather than `useState` so nothing re-renders, and the counter is
 * only ever read once per mounted instance. The id reaches the DOM as an
 * attribute other nodes point at — the Select search input's `id`, which a
 * `Form.Item`'s `<label htmlFor>` targets, and the Popconfirm panel's
 * `aria-labelledby`/`aria-describedby` targets — so it must be unique per
 * instance and must not change between renders.
 *
 * Leaf module: the overlay seams that use it are reachable from boot-static
 * code (the PRO-130 rule in AGENTS.md), so it imports nothing but React.
 */
let counter = 0

export const useStableId = (prefix: string): string => {
  const ref = React.useRef<string>()
  if (!ref.current) {
    counter += 1
    ref.current = `${prefix}-${counter}`
  }
  return ref.current
}
